import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { extractErrorMessage } from "@/lib/api";
import logoIcon from "@/assets/brand/logo-icon.svg";

const search = z.object({ redirect: z.string().optional() });

export const Route = createFileRoute("/login")({
  validateSearch: search,
  beforeLoad: () => {
    // If already authenticated, redirect to home. Auth state lives in localStorage,
    // so check synchronously via the store.
    if (typeof window !== "undefined" && window.localStorage.getItem("CavaYo:auth-token")) {
      throw redirect({ to: "/" });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const search = Route.useSearch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login({ email, password });
      toast.success("Welcome back!");
      navigate({ to: search.redirect ?? "/" });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Invalid credentials"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/10 via-background to-accent/10 px-4 py-12">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-[var(--shadow-elevated)]">
        <Link to="/" className="mb-6 flex flex-col items-center gap-2">
          <img src={logoIcon} alt="CavaYo" className="h-12 w-12 rounded-xl object-contain" />
          {/*
            One heading, not an <h3> nested inside an <h2>. The "@" is a
            typographic flourish in the middle of a phrase — a span, not a
            second-level section of the first heading.
          */}
          <h1 className="font-display text-lg font-semibold text-primary">
            Feel <span className="align-middle text-ink">@</span> Home
          </h1>
        </Link>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <Field label="Email">
            {/*
              `autoCapitalize` and `spellCheck` off because a phone keyboard
              will otherwise capitalise the first letter of an address and
              underline the whole thing in red. `enterKeyHint` labels the
              keyboard's action key "next" instead of "go", so the return key
              moves to the password field rather than submitting an empty form.
            */}
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="next"
              className="auth-input"
            />
          </Field>
          <Field label="Password">
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              enterKeyHint="go"
              className="auth-input"
            />
          </Field>

          <button
            type="submit"
            disabled={submitting}
            className="h-12 w-full rounded-xl bg-primary text-sm font-bold text-primary-foreground transition hover:opacity-90 disabled:opacity-60"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-ink-muted">
          Don't have an account?{" "}
          <Link to="/register" className="font-semibold text-primary hover:underline">
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-muted">
        {label}
      </span>
      {children}
    </label>
  );
}
